"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@heroui/react";
import CareerJobCard, { type JobListing } from "@/components/page-data/Career/CareerJobCard";
import { useGetJobsPostingDetailsAPIQuery } from "@/redux/rtkQueries/clientSideGetApis";
import type { JobsPostingDataEntity } from "@/types/GetJobsPostingListApiType";

const PAGE_SIZE = 10;

function CareerJobRowShimmer() {
    return (
        <div className="relative flex flex-col gap-6 py-8 md:flex-row md:items-center md:justify-between md:gap-20">
            <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative h-7 w-[min(280px,70%)] overflow-hidden rounded-md bg-gray-200">
                        <span className="shimmer absolute inset-0" />
                    </div>
                    <div className="relative h-6 w-24 overflow-hidden rounded-full bg-gray-200">
                        <span className="shimmer absolute inset-0" />
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="relative h-4 w-full overflow-hidden rounded bg-gray-200">
                        <span className="shimmer absolute inset-0" />
                    </div>
                    <div className="relative h-4 w-[90%] overflow-hidden rounded bg-gray-200">
                        <span className="shimmer absolute inset-0" />
                    </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                    <div className="relative h-4 w-36 overflow-hidden rounded bg-gray-200">
                        <span className="shimmer absolute inset-0" />
                    </div>
                    <div className="relative h-4 w-28 overflow-hidden rounded bg-gray-200">
                        <span className="shimmer absolute inset-0" />
                    </div>
                </div>
            </div>
            <div className="relative h-5 w-28 shrink-0 self-start overflow-hidden rounded bg-gray-200 md:self-center">
                <span className="shimmer absolute inset-0" />
            </div>
        </div>
    );
}

function mapPostingToJob(entity: JobsPostingDataEntity): JobListing {
    const description =
        entity.about?.filter(Boolean).join(" ").trim() || "View role for full details.";
    return {
        id: entity._id,
        slug: entity.slug,
        title: entity.title,
        department: entity.department?.trim() || "Open role",
        description,
        location: entity.location?.trim() || "—",
        salaryRange: entity.salary?.trim() || "—",
        href: entity.mapLink?.trim() || "#",
    };
}

export default function CareerJobOpeningsList() {
    const [page, setPage] = useState(1);
    const [listings, setListings] = useState<JobListing[]>([]);

    const { data, isLoading, isError, isFetching } = useGetJobsPostingDetailsAPIQuery({
        pageNumber: page,
        limit: PAGE_SIZE,
    });

    useEffect(() => {
        if (isError || data == null) return;
        const rows = data.data;
        if (!Array.isArray(rows)) return;
        const mapped = rows.filter((j) => j.isActive).map(mapPostingToJob);
        setListings((prev) => {
            if (page === 1) return mapped;
            const seen = new Set(prev.map((j) => j.id));
            const merged = mapped.filter((j) => !seen.has(j.id));
            return [...prev, ...merged];
        });
    }, [data, page, isError]);

    const hasMore = useMemo(() => {
        if (!data) return false;
        const totalPages = data.totalPages;
        if (typeof totalPages === "number" && totalPages > 0) {
            return page < totalPages;
        }
        const raw = data.data;
        if (!Array.isArray(raw)) return false;
        return raw.length >= PAGE_SIZE;
    }, [data, page]);

    const showInitialLoading = isLoading && page === 1 && listings.length === 0;

    const loadMore = useCallback(() => {
        if (!hasMore || isFetching) return;
        setPage((p) => p + 1);
    }, [hasMore, isFetching]);

    if (showInitialLoading) {
        return (
            <div
                className="mx-auto w-full divide-y divide-borderColor"
                aria-busy="true"
                aria-label="Loading open positions"
            >
                {Array.from({ length: PAGE_SIZE }, (_, i) => (
                    <CareerJobRowShimmer key={`career-job-shimmer-${i}`} />
                ))}
            </div>
        );
    }

    if (isError && listings.length === 0) {
        return (
            <div className="py-12 text-center text-sm text-danger md:text-base">
                Could not load job listings. Please try again later.
            </div>
        );
    }

    if (!listings.length && !isFetching) {
        return (
            <div className="py-12 text-center text-sm text-textf2 md:text-base">No open positions at the moment.</div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="mx-auto w-full divide-y divide-borderColor">
                {listings.map((job) => (
                    <CareerJobCard key={job.id} job={job} />
                ))}
            </div>
            {hasMore ? (
                <div className="flex justify-center">
                    <Button
                        type="button"
                        variant="bordered"
                        radius="full"
                        className="min-w-[160px] border-lightSliver font-medium text-textf1"
                        onPress={loadMore}
                        isLoading={isFetching}
                        isDisabled={isFetching}
                    >
                        Load more
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
